import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/_Shared/Button";
import { Submit } from "@/components/_Shared/Form/Buttons/Submit";
import {
	FormChild,
	FormGroup,
} from "@/components/_Shared/Form/Containers/FormGroup";
import { Checkbox } from "@/components/_Shared/Form/Inputs/Checkbox";
import { Input } from "@/components/_Shared/Form/Inputs/Input";
import { SearchSelect } from "@/components/_Shared/Form/Inputs/SearchSelect";
import { SliderWithInput } from "@/components/_Shared/Form/Inputs/Slider";
import { TextArea } from "@/components/_Shared/Form/Inputs/TextArea";
import { FormFetchDataProps } from "@/components/_Shared/Form/Interfaces/FormFetchData";
import { GenericForm, GenericFormBGContainer } from "@/components/GenericForm";
import { Divider } from "@/components/misc";
import { TaskProps } from "@/interfaces/tasks_types";
import { FormDataType } from "./TaskForm";

type TaskFormBaseProps = {
	project_list: FormFetchDataProps[];
	manager_list: FormFetchDataProps[];
	category_list: FormFetchDataProps[];
	currentTaskData: TaskProps;
	method: "POST" | "PUT";
	isAssignment: boolean;
	setAssignment: (isAssignment: boolean) => void;
	register: UseFormRegister<FormDataType>;
	errors: FieldErrors<FormDataType>;
	control: Control<FormDataType>;
	isSubmitting: boolean;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};

export function TaskFormBase({
	project_list,
	manager_list,
	category_list,
	currentTaskData,
	method,
	isAssignment,
	setAssignment,
	register,
	errors,
	control,
	isSubmitting,
	onSubmit,
}: TaskFormBaseProps) {
	const navigate = useNavigate();

	const method_string = {
		POST: "Create Task",
		PUT: "Update Task",
	};

	const status_options = ["Active", "Closed"].map((status) => ({
		value: status,
		label: status,
	}));

	return (
		<GenericForm form_id="task_form" onSubmit={onSubmit}>
			<GenericFormBGContainer
				className={
					isAssignment
						? " bg-red-50 transition-all duration-300"
						: " transition-all duration-300"
				}
			>
				{isAssignment ? (
					<h2 className="text-2xl font-bold text-red-800">
						{method == "POST" ? "Create" : "Update"} Assignment
					</h2>
				) : (
					<h2 className="text-2xl font-bold">{method_string[method]}</h2>
				)}

				<p className="text-orange-500">
					If you want to create a task that is not related to a project, you can
					check the assignment box.
				</p>

				<Divider />

				{!isAssignment && (
					<SearchSelect
						label="Project"
						name="project"
						options={project_list}
						control={control}
						readonly={method === "PUT"}
					/>
				)}
				{errors.project && (
					<p className="text-red-500">{errors.project.message}</p>
				)}

				{method == "POST" && (
					<FormGroup className="flex flex-row items-center gap-2">
						<label htmlFor="is_assignment">Assignment</label>
						<input
							id="is_assignment"
							type="checkbox"
							name="is_assignment"
							checked={isAssignment}
							onChange={() => setAssignment(!isAssignment)}
						/>
					</FormGroup>
				)}

				<Input label="Title" name="title" type="text" register={register} />
				{errors.title && <p className="text-red-500">{errors.title.message}</p>}

				<Input
					label="Due Date"
					name="due_date"
					type="date"
					register={register}
				/>

				<FormGroup>
					<FormChild>
						<SearchSelect
							label="Assigned By"
							name="assigned_by"
							options={manager_list}
							control={control}
						/>
						{errors.assigned_by && (
							<p className="text-red-500">{errors.assigned_by.message}</p>
						)}
					</FormChild>

					<FormChild>
						<SearchSelect
							label="Assigned To"
							name="assigned_to"
							options={manager_list}
							control={control}
						/>
						{errors.assigned_to && (
							<p className="text-red-500">{errors.assigned_to.message}</p>
						)}
					</FormChild>
				</FormGroup>

				<FormGroup>
					<FormChild>
						<SearchSelect
							label="Category"
							name="category"
							options={category_list}
							control={control}
						/>
						{errors.category && (
							<p className="text-red-500">{errors.category.message}</p>
						)}
					</FormChild>
					<FormChild>
						<SearchSelect
							label="Status"
							name="status"
							options={status_options}
							control={control}
						/>
					</FormChild>
				</FormGroup>

				<FormGroup>
					<FormChild layout="row" className="col-start-2">
						<Checkbox label="Urgent?" name="priority" register={register} />
					</FormChild>
				</FormGroup>

				<TextArea
					label="Task Description"
					name="description"
					placeholder="Enter a description for the task"
					register={register}
				/>
				{errors.description && (
					<p className="text-red-500">{errors.description.message}</p>
				)}

				<SliderWithInput
					label="Completion Percentage"
					required
					defaultValue={currentTaskData.completion_percentage}
					max={100}
					min={0}
					step={1}
					name="completion_percentage"
					control={control}
				/>

				<FormGroup>
					<Button className="" onClick={() => navigate("/tasks")}>
						Cancel
					</Button>
					<Submit isSubmitting={isSubmitting} label={method_string[method]} />
				</FormGroup>
			</GenericFormBGContainer>
		</GenericForm>
	);
}
